/**
 * Mall Activity Map — interior floor plan with live tenant-zone highlighting.
 *
 * Lower/Upper level toggle, each backed by the real (cropped) Taubman leasing
 * plan. Tenant spaces are traced polygons (from the Zone Editor, stored in D1).
 * A zone AUTO-HIGHLIGHTS red when it has a call today; clicking any zone shows
 * that suite's calls in a side panel. Until a suite is traced it simply has no
 * zone yet — trace it in the Zone Editor.
 *
 * Level is derived from the address suffix (L = lower, U = upper).
 */
import { useState, useEffect, useMemo } from 'react'
import { useCadStore, type CadCall } from '../../stores/cad'
import { workerGetV1 } from '../../api/worker'

interface Pt { x: number; y: number }
interface Zone { wing: string; space: string; level: string; points: string }

function fullStamp(ts: string): string {
  if (!ts) return ''
  const [d, t] = ts.split(' ')
  if (!d) return ts
  const [y, m, day] = d.split('-')
  return `${m}/${day}/${y}${t ? ' ' + t.slice(0, 5) : ''}`
}

export default function MallActivityMap() {
  const { calls, loadCalls, businesses, loadRefs } = useCadStore()
  const [level, setLevel] = useState<'L' | 'U'>('L')
  const [zones, setZones] = useState<Zone[]>([])
  const [selected, setSelected] = useState<string | null>(null) // "wing|space"

  useEffect(() => {
    loadRefs(); loadCalls()
    void (async () => {
      try {
        const res = await workerGetV1<{ ok: boolean; zones: Zone[] }>('/api/v1/cad/zones')
        setZones(res.zones || [])
      } catch { /* ignore */ }
    })()
    const iv = setInterval(() => loadCalls(), 20_000)
    return () => clearInterval(iv)
  }, [loadRefs, loadCalls])

  // Map business name -> {wing, space} so a call resolves to a suite/zone
  const bizByName = useMemo(() => new Map(businesses.map(b => [b.name, b])), [businesses])

  // Calls grouped by suite key "wing|space" for the active level
  const callsBySuite = useMemo(() => {
    const map = new Map<string, CadCall[]>()
    for (const c of calls) {
      const biz = bizByName.get(c.business_name)
      if (!biz?.wing || !biz?.space) continue
      const lvl = (c.location || biz.address || '').toUpperCase().includes('U') ? 'U' : 'L'
      if (lvl !== level) continue
      const key = `${biz.wing}|${biz.space}`
      const arr = map.get(key) || []
      arr.push(c)
      map.set(key, arr)
    }
    return map
  }, [calls, bizByName, level])

  const levelZones = useMemo(() => zones.filter(z => z.level === level), [zones, level])
  const img = level === 'L' ? '/floor-lower.png' : '/floor-upper.png'
  const toPoly = (pts: Pt[]) => pts.map(p => `${p.x},${p.y}`).join(' ')

  const selectedCalls = selected ? (callsBySuite.get(selected) || []) : []
  const totalThisLevel = Array.from(callsBySuite.values()).reduce((a, arr) => a + arr.length, 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0 10px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Mall Activity Map</h1>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Today's calls by tenant &middot; {totalThisLevel} on this level</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['L', 'U'] as const).map(l => (
            <button key={l} onClick={() => { setLevel(l); setSelected(null) }}
              style={{ padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, border: '1px solid var(--border)',
                background: level === l ? 'var(--accent)' : 'var(--bg-elevated)', color: level === l ? '#fff' : 'var(--text-primary)' }}>
              {l === 'L' ? 'Lower' : 'Upper'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
        {/* Map */}
        <div style={{ position: 'relative', flex: 1, minHeight: 0, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={img} alt="floor plan" style={{ display: 'block', maxWidth: '100%' }} draggable={false} />
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              {levelZones.map((z, i) => {
                let pts: Pt[] = []
                try { pts = JSON.parse(z.points) } catch { /* ignore */ }
                if (pts.length < 3) return null
                const key = `${z.wing}|${z.space}`
                const n = (callsBySuite.get(key) || []).length
                const isSel = selected === key
                const fill = n > 0 ? 'rgba(224,90,90,0.45)' : isSel ? 'rgba(201,168,76,0.3)' : 'rgba(120,120,120,0.08)'
                const stroke = n > 0 ? '#e05a5a' : isSel ? 'var(--accent)' : 'rgba(150,150,150,0.4)'
                return (
                  <polygon key={i} points={toPoly(pts)} fill={fill} stroke={stroke} strokeWidth={isSel ? '0.4' : '0.2'}
                    style={{ cursor: 'pointer' }} onClick={() => setSelected(key)} />
                )
              })}
            </svg>
          </div>
        </div>

        {/* Side panel: selected suite's calls */}
        {selected && (
          <div style={{ width: 260, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{selected.replace('|', '-')}</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer' }}>&times;</button>
            </div>
            {selectedCalls.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No calls here today.</div>
              : selectedCalls.map(c => (
                  <div key={c.id} style={{ borderLeft: '3px solid #e05a5a', padding: '4px 8px', marginBottom: 6, background: 'var(--bg-elevated)', borderRadius: 4 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>{c.type_call}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fullStamp(c.received_ts)}{c.unit_assigned ? ` · ${c.unit_assigned}` : ''}</div>
                  </div>
                ))}
          </div>
        )}
      </div>

      {levelZones.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          No tenant zones traced for this level yet. Use the Zone Editor to trace suites.
        </div>
      )}
    </div>
  )
}
